import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setSignupData,
  setError,
  setVerificationStep,
} from "../../store/slices/signupSlice";
import CustomDropdown from "../common/CustomDropdown";
import { authService } from "../../services/api";

const OrganizerDetailsStep = () => {
  const dispatch = useDispatch();
  const { error } = useSelector((state) => state.signup);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    organizationName: "",
    organizationType: "",
    contactPersonName: "",
    licenseNumber: "",
    address: "",
    city: "",
    country: "",
    zipcode: "",
  });

  const organizationTypes = [
    { value: "Tournament", label: "Tournament" },
    { value: "League", label: "League" },
    { value: "Football Association", label: "Football Association" },
    { value: "Sports Federation", label: "Sports Federation" },
    { value: "Academy", label: "Academy" },
    { value: "Other", label: "Other" },
  ];

  const handleBack = () => {
    dispatch(setVerificationStep("type"));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.organizationName ||
      !formData.organizationType ||
      !formData.contactPersonName ||
      !formData.licenseNumber
    ) {
      dispatch(setError("Please fill in all required fields"));
      return;
    }

    setLoading(true);
    dispatch(setError(null));

    const organizerData = {
      name: formData.organizationName,
      can_organize_matches: true,
      organizer_type: formData.organizationType,
      confideration: "UEFA", // Default value as per API spec
      address: formData.address || "",
      city: formData.city || "",
      country: formData.country || "",
      zipcode: formData.zipcode || "",
    };

    console.log("Submitting organizer data:", organizerData);

    try {
      const response = await authService.createOrganizer(organizerData);

      if (response.success) {
        dispatch(setSignupData({ organizerDetails: organizerData }));
        dispatch(setVerificationStep("complete"));
      } else {
        dispatch(
          setError(response.error || "Failed to create organizer profile")
        );
      }
    } catch (error) {
      console.error("Submit error:", error);
      dispatch(setError("An unexpected error occurred"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-center mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-5 h-5 mr-2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 19.5L8.25 12l7.5-7.5"
            />
          </svg>
          Back
        </button>
      </div>

      <div className="w-full">
        <div className="flex items-center justify-center w-10 h-10 bg-[#374DF5] rounded-lg mb-4">
          <svg
            width="14"
            height="13"
            viewBox="0 0 14 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.26144 11.5897V10.4588C9.26144 9.85886 9.02313 9.28353 8.59894 8.85934C8.17475 8.43515 7.59943 8.19685 6.99953 8.19685H3.0412C2.44131 8.19685 1.86598 8.43515 1.44179 8.85934C1.0176 9.28353 0.779297 9.85886 0.779297 10.4588V11.5897M11.5233 4.23851V7.63137M13.2198 5.93494H9.82692M7.28227 3.67304C7.28227 4.92225 6.26958 5.93494 5.02037 5.93494C3.77115 5.93494 2.75846 4.92225 2.75846 3.67304C2.75846 2.42382 3.77115 1.41113 5.02037 1.41113C6.26958 1.41113 7.28227 2.42382 7.28227 3.67304Z"
              stroke="white"
              stroke-width="1.32577"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <h2 className="text-2xl leading-[28px] font-bold text-gray-900 mb-2">
          Organizer Details
        </h2>
        <p className="text-[14px] leading-[20px] text-[#6C737F] mb-8">
          Please provide your organization information
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block font-medium text-[#000000] text-base mb-1">
              Organization Name
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]"
              placeholder="Enter organization name"
              value={formData.organizationName}
              onChange={(e) =>
                handleInputChange("organizationName", e.target.value)
              }
              required
            />
          </div>

          <div>
            <CustomDropdown
              label="Organization Type"
              labelClassName="block font-medium text-[#000000] text-base mb-1"
              options={organizationTypes}
              value={formData.organizationType}
              onChange={(value) => handleInputChange("organizationType", value)}
              placeholder="Select organization type"
              displayProperty="label"
              valueProperty="value"
              searchEnabled={true}
              searchPlaceholder="Search organization type..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]"
            />
          </div>

          <div>
            <label className="block font-medium text-[#000000] text-base mb-1">
              Contact Person Name
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]"
              placeholder="Enter contact person name"
              value={formData.contactPersonName}
              onChange={(e) =>
                handleInputChange("contactPersonName", e.target.value)
              }
              required
            />
          </div>

          <div>
            <label className="block font-medium text-[#000000] text-base mb-1">
              License Number
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]"
              placeholder="Enter license number"
              value={formData.licenseNumber}
              onChange={(e) =>
                handleInputChange("licenseNumber", e.target.value)
              }
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2 px-4 rounded-lg transition-colors mb-4 ${
              loading
                ? "bg-[#2283FF1A] text-[#2283FF] cursor-not-allowed"
                : "bg-[#2283FF] hover:bg-[#1a6cd4] text-white"
            }`}
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </form>
      </div>
    </>
  );
};

export default OrganizerDetailsStep;
